/**
 * SA-1029 (spec §D19–§D21): the browser's half of the realtime notice. One
 * subscription (AppShell) to the inbox event bus; on a customer-update event
 * the record's React Query reads and the task lists are invalidated, the
 * task page's notice is kept or cleared (a viewer on a task page of that
 * record sees the banner, never a toast), and a viewer elsewhere who is
 * interested gets one toast per update with a link to the record's Intake.
 * The record page's own hand-rolled reads refetch in OrdersPage (its lane).
 */
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useToast } from '@/components/ui/Toast'
import { REVIEW_IN_INTAKE, REVIEW_IN_QUOTES, responseHref, responseToastTitle, toastTitle, updateHref } from '@/components/records/customerUpdateText'
import { isInterested, noticeTarget } from '@/services/recordUpdateNotices'
import { subscribeInboxEvents } from '@/services/inboxEventBus'
import { useAuthStore } from '@/stores/authStore'
import { useRecordUpdateNoticeStore } from '@/stores/recordUpdateNoticeStore'
import type { TaskRead } from '@/types/process'

/** The task page's id, by the URL (`/tasks/:taskId`); null elsewhere. */
function taskIdOf(pathname: string): string | null {
  const match = /^\/tasks\/([^/]+)/.exec(pathname)
  return match ? decodeURIComponent(match[1]) : null
}

export function useRecordUpdateNotices(): void {
  const qc = useQueryClient()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const here = useRef(location)
  here.current = location
  const toasted = useRef(new Set<string>())

  useEffect(() => subscribeInboxEvents((e) => {
    if ((e.type !== 'record_update_received' && e.type !== 'record_update_settled') || !e.orderId || !e.updateId) return
    const orderId = e.orderId
    // The task page's record comes from its own cached read: the URL names
    // only the task (spec D19 row 2).
    const taskId = taskIdOf(here.current.pathname)
    const taskOrderId = taskId ? qc.getQueryData<TaskRead>(['process', 'task', taskId])?.order_id ?? null : null
    const target = noticeTarget(here.current, orderId, taskOrderId)
    // The record's own reads and the task lists — never every process query
    // in every tab (spec D20).
    void qc.invalidateQueries({ queryKey: ['records', orderId] })
    void qc.invalidateQueries({ queryKey: ['process', 'record', orderId] })
    void qc.invalidateQueries({ queryKey: ['process', 'record-tasks', orderId] })
    void qc.invalidateQueries({ queryKey: ['process', 'tasks'] })
    if (target === 'task' && taskId) void qc.invalidateQueries({ queryKey: ['process', 'task', taskId] })
    const store = useRecordUpdateNoticeStore.getState()
    if (e.type === 'record_update_settled') {
      store.clear(orderId)
      return
    }
    const kind = e.kind === 'response' ? 'response' : 'update'
    store.set(orderId, { updateId: e.updateId, orderNumber: e.orderNumber ?? '', kind })
    // On the record the refetch is the notice; on its task page the banner is.
    if (target !== 'elsewhere') return
    const user = useAuthStore.getState().user
    const viewer = user ? { id: user.id, roles: user.roles ?? [] } : null
    if (!isInterested(viewer, e.interest) || toasted.current.has(e.updateId)) return
    toasted.current.add(e.updateId)
    const response = kind === 'response'
    const href = response ? responseHref(orderId) : updateHref(orderId)
    addToast({
      type: 'warning', title: (response ? responseToastTitle : toastTitle)(e.orderNumber ?? orderId), duration: 10000,
      action: { label: response ? REVIEW_IN_QUOTES : REVIEW_IN_INTAKE, onClick: () => navigate(href) },
    })
  }), [qc, addToast, navigate])
}
